export interface BlogPost {
  title: string
  slug: string
  author: string
  authorInitials: string
  date: string
  readTime: string
  tags: string[]
  status: 'published' | 'draft'
  excerpt: string
  content: string
}

const tagColors: Record<string, { bg: string; text: string }> = {
  Research:       { bg: 'rgba(27,94,255,0.12)',   text: '#60A5FA' },
  Engineering:    { bg: 'rgba(124,58,237,0.12)',  text: '#A78BFA' },
  Infrastructure: { bg: 'rgba(124,58,237,0.12)',  text: '#A78BFA' },
  Perspective:    { bg: 'rgba(249,115,22,0.12)',  text: '#FB923C' },
  JEPA:           { bg: 'rgba(27,94,255,0.12)',   text: '#60A5FA' },
  Founding:       { bg: 'rgba(249,115,22,0.12)',  text: '#FB923C' },
}

export function getTagStyle(tag: string) {
  return tagColors[tag] ?? { bg: 'rgba(123,132,153,0.12)', text: '#9BA4B5' }
}

function parseFrontmatter(raw: string): { frontmatter: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
  if (!match) return { frontmatter: {}, content: raw }
  const lines = match[1].split('\n')
  const frontmatter: Record<string, unknown> = {}
  for (const line of lines) {
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) continue
    const key = line.slice(0, colonIdx).trim()
    const val = line.slice(colonIdx + 1).trim()
    if (val.startsWith('[')) {
      try { frontmatter[key] = JSON.parse(val) } catch { frontmatter[key] = val }
    } else {
      frontmatter[key] = val.replace(/^["']|["']$/g, '')
    }
  }
  return { frontmatter, content: match[2].trim() }
}

const rawModules = import.meta.glob('/src/content/posts/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>

function parseAllPosts(): BlogPost[] {
  const posts: BlogPost[] = []
  for (const [, raw] of Object.entries(rawModules)) {
    const { frontmatter, content } = parseFrontmatter(raw)
    const fm = frontmatter as Record<string, string>
    posts.push({
      title: fm.title ?? '',
      slug: fm.slug ?? '',
      author: fm.author ?? '',
      authorInitials: fm.authorInitials ?? '',
      date: fm.date ?? '',
      readTime: fm.readTime ?? '',
      tags: (frontmatter.tags as string[]) ?? [],
      status: (fm.status as 'published' | 'draft') ?? 'published',
      excerpt: fm.excerpt ?? '',
      content,
    })
  }
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

export async function getAllPosts(): Promise<BlogPost[]> {
  return parseAllPosts().filter(p => p.status === 'published')
}

export async function getAllPostsAdmin(): Promise<BlogPost[]> {
  return parseAllPosts()
}

export function renderMarkdown(md: string): string {
  // Split into blocks on double newlines
  const blocks = md.split(/\n\n+/)
  const htmlBlocks = blocks.map(block => {
    const trimmed = block.trim()
    if (!trimmed) return ''

    // Headings — block level, not wrapped in <p>
    if (/^## /.test(trimmed)) {
      return trimmed.replace(/^## (.+)$/, '<h2 class="text-h3 text-pure mt-10 mb-4">$1</h2>')
    }
    if (/^### /.test(trimmed)) {
      return trimmed.replace(/^### (.+)$/, '<h3 class="text-title-1 text-pure mt-8 mb-3">$1</h3>')
    }

    // Horizontal rule
    if (/^---$/.test(trimmed)) {
      return '<hr style="border:none;border-top:1px solid rgba(30,32,40,0.8);margin:40px 0;" />' 
    }

    // Image / Video stubs: ![caption](stub)
    const imgMatch = trimmed.match(/^!\[([^\]]*?)\]\(([^)]+)\)$/)
    if (imgMatch) {
      const caption = imgMatch[1]
      const isVideo = caption.startsWith('VIDEO:')
      const label = isVideo ? '[ VIDEO PLACEHOLDER ]' : '[ IMAGE PLACEHOLDER ]'
      const displayCaption = isVideo ? caption.replace(/^VIDEO:\s*/, '') : caption
      return `<figure class="blog-image-stub my-10">
  <div style="background:rgba(30,32,40,0.8);border:1px dashed rgba(114,121,138,0.4);border-radius:4px;padding:48px 24px;text-align:center;">
    <div style="font-family:'JetBrains Mono',monospace;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:rgba(114,121,138,0.6);margin-bottom:12px;">${label}</div>
    <p style="font-size:14px;color:rgba(230,233,244,0.5);line-height:1.6;max-width:480px;margin:0 auto;">${displayCaption}</p>
  </div>
</figure>`
    }

    // Blockquote: lines starting with "> "
    if (/^> /.test(trimmed)) {
      const text = trimmed.replace(/^> /, '')
      return `<blockquote style="border-left:2px solid rgba(32,88,240,0.5);padding-left:20px;margin:24px 0;">
  <p style="font-style:italic;color:rgba(230,233,244,0.65);font-size:17px;line-height:1.7;margin:0;">${text}</p>
</blockquote>`
    }

    // Bullet list: lines starting with "- "
    const lines = trimmed.split('\n')
    if (lines.every(l => /^- /.test(l))) {
      const items = lines.map(l => {
        const itemText = l.replace(/^- /, '')
        return `  <li style="display:flex;gap:10px;margin-bottom:8px;color:rgba(230,233,244,0.75);font-size:17px;line-height:1.6;">
    <span style="color:#2058F0;flex-shrink:0;margin-top:2px;">—</span>
    <span>${itemText}</span>
  </li>`
      }).join('\n')
      return `<ul style="list-style:none;padding:0;margin:0 0 16px;">
${items}
</ul>`
    }

    // Inline formatting within paragraph blocks
    const html = trimmed
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-pure font-semibold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
    return `<p class="text-body text-steel leading-relaxed mb-4">${html}</p>`
  })
  return htmlBlocks.filter(Boolean).join('\n')
}
